import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, MessageCircle, Navigation } from 'lucide-react';
import { clinicInfo, faqs } from '../data/mock';

// Healthwrx exact location
const CLINIC_LAT = 26.13932093452639;
const CLINIC_LNG = 91.7890232810652;
const MAP_EMBED = `https://maps.google.com/maps?q=${CLINIC_LAT},${CLINIC_LNG}&z=17&output=embed`;
const MAP_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${CLINIC_LAT},${CLINIC_LNG}`;
const WHATSAPP_URL = `https://wa.me/${(clinicInfo.phone || '').replace(/[^\d]/g, '') || '919992604456'}`;

export const Contact = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Get in Touch</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            We&apos;re here to answer your questions and provide the care you need.
          </p>
        </div>
      </section>

      {/* Contact Info — full-width grid */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#14D1A2]/10 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#14D1A2]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Visit Us</h3>
                <p className="text-gray-600 leading-relaxed">
                  {clinicInfo.address.line1}<br />
                  {clinicInfo.address.line2}<br />
                  {clinicInfo.address.city} {clinicInfo.address.pincode}
                </p>
                <a
                  href={MAP_DIRECTIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#14D1A2] hover:text-[#10B88A] text-sm font-medium mt-3 inline-flex items-center gap-1"
                  data-testid="get-directions-link"
                >
                  <Navigation className="w-3.5 h-3.5" /> Get Directions
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#14D1A2]/10 rounded-lg flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-[#14D1A2]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Us</h3>
                <a href={`tel:${clinicInfo.phone}`} className="text-gray-600 hover:text-[#14D1A2] block mb-1">
                  {clinicInfo.phone}
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  Mon - Fri: {clinicInfo.hours.weekdays}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">WhatsApp</h3>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-green-700"
                >
                  Chat with our team
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  Quickest way to reach us
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#14D1A2]/10 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-[#14D1A2]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
                <a href={`mailto:${clinicInfo.email}`} className="text-gray-600 hover:text-[#14D1A2] break-words">
                  {clinicInfo.email}
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  We respond within 24 hours
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Opening hours strip */}
          <Card className="border-0 shadow-lg bg-[#1C5C62] text-white mt-6">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">Opening Hours</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                  <div className="flex justify-between sm:justify-start sm:gap-3">
                    <span>Mon - Fri</span>
                    <span className="text-gray-300">{clinicInfo.hours.weekdays}</span>
                  </div>
                  <div className="flex justify-between sm:justify-start sm:gap-3">
                    <span>Saturday</span>
                    <span className="text-gray-300">{clinicInfo.hours.saturday}</span>
                  </div>
                  <div className="flex justify-between sm:justify-start sm:gap-3">
                    <span>Sunday</span>
                    <span className="text-gray-300">{clinicInfo.hours.sunday}</span>
                  </div>
                </div>
                <Link to="/book-appointment">
                  <Button className="bg-[#14D1A2] hover:bg-[#10B88A] text-white" data-testid="contact-cta-appointment">
                    Request Appointment
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0">
        <div className="container mx-auto px-4">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src={MAP_EMBED}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Healthwrx Location"
              data-testid="contact-map-iframe"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Quick answers to common questions about our clinic
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-[#14D1A2] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;
